"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignalConsolidationMapSchema = exports.SignalConsolidationEntrySchema = void 0;
exports.applySignalConsolidation = applySignalConsolidation;
const z = require("zod");
const signal_set_comparison_1 = require("../comparisons/signal-set.comparison");
exports.SignalConsolidationEntrySchema = z.object({
    sourceKey: z.string(),
    canonicalName: z.string().nullable(),
});
exports.SignalConsolidationMapSchema = exports.SignalConsolidationEntrySchema.array();
function isRelativeSignalRef(value) {
    return (value !== null && typeof value === 'object' && ('signal' in value || 'signal_name' in value));
}
function transformRelativeSignalRef(ref, droppedIds, droppedKeys, renameByKey) {
    const ids = ref.signal === undefined ? []
        : Array.isArray(ref.signal) ? ref.signal
            : [ref.signal];
    const keptIds = ids.filter((id) => !droppedIds.has(String(id)));
    const names = ref.signal_name === undefined ? []
        : Array.isArray(ref.signal_name) ? ref.signal_name
            : [ref.signal_name];
    const keptNames = names
        .filter((name) => !droppedKeys.has((0, signal_set_comparison_1.signalSetRowKeyForName)(name)))
        .map((name) => renameByKey.get((0, signal_set_comparison_1.signalSetRowKeyForName)(name)) ?? name);
    if (ids.length + names.length > 0 && keptIds.length + keptNames.length === 0) {
        return null;
    }
    const next = {};
    if (ref.signal !== undefined && keptIds.length > 0) {
        next.signal = Array.isArray(ref.signal) ? keptIds : keptIds[0];
    }
    if (ref.signal_name !== undefined && keptNames.length > 0) {
        next.signal_name = Array.isArray(ref.signal_name) ? keptNames : keptNames[0];
    }
    return next;
}
function transformBoundaryDefinition(definition, droppedIds, droppedKeys, renameByKey) {
    const next = { ...definition };
    const writable = next;
    for (const field of ['target', 'relative_to']) {
        const value = next[field];
        if (!isRelativeSignalRef(value)) {
            continue;
        }
        const transformed = transformRelativeSignalRef(value, droppedIds, droppedKeys, renameByKey);
        if (transformed === null) {
            return null;
        }
        writable[field] = transformed;
    }
    return next;
}
function applySignalConsolidation(payload, map) {
    const entryByKey = new Map(map.map((entry) => [entry.sourceKey, entry]));
    const renameByKey = new Map(map
        .filter((entry) => entry.canonicalName !== null)
        .map((entry) => [entry.sourceKey, entry.canonicalName]));
    const droppedKeys = new Set(map.filter((entry) => entry.canonicalName === null).map((entry) => entry.sourceKey));
    const droppedIds = new Set();
    const renamed = [];
    for (const signal of payload.signals) {
        const key = (0, signal_set_comparison_1.signalSetRowKeyForName)(signal.name);
        const entry = entryByKey.get(key);
        if (entry === undefined) {
            renamed.push(signal);
        }
        else if (entry.canonicalName === null) {
            droppedIds.add(String(signal.id));
        }
        else if (entry.canonicalName === signal.name) {
            renamed.push(signal);
        }
        else {
            renamed.push({ ...signal, name: entry.canonicalName });
        }
    }
    const seenNameKeys = new Set();
    const duplicateNameIds = [];
    const signals = [];
    for (const signal of renamed) {
        const nameKey = (0, signal_set_comparison_1.signalSetRowKeyForName)(signal.name);
        if (seenNameKeys.has(nameKey)) {
            droppedIds.add(String(signal.id));
            duplicateNameIds.push(String(signal.id));
            continue;
        }
        seenNameKeys.add(nameKey);
        signals.push(signal);
    }
    let tieCount = 0;
    const signalsWithTies = signals.map((signal) => {
        const ties = signal.ties ?? [];
        const keptTies = ties.filter((tie) => tie.rootComponentSignalId === undefined || !droppedIds.has(String(tie.rootComponentSignalId)));
        tieCount += ties.length - keptTies.length;
        return keptTies.length === ties.length ? signal : { ...signal, ties: keptTies };
    });
    const pinout = Object.fromEntries(Object.entries(payload.pinout).filter(([signalId]) => !droppedIds.has(signalId)));
    const droppedBoundaryIds = [];
    const boundaries = [];
    for (const boundary of payload.boundaries) {
        const definition = transformBoundaryDefinition(boundary.boundary, droppedIds, droppedKeys, renameByKey);
        if (definition === null) {
            droppedBoundaryIds.push(boundary.id);
            continue;
        }
        boundaries.push(definition === boundary.boundary ? boundary : { ...boundary, boundary: definition });
    }
    let functionRefCount = 0;
    const functions = payload.functions.map((componentFunction) => ({
        ...componentFunction,
        functionSignals: componentFunction.functionSignals.map((functionSignal) => {
            const kept = functionSignal.signals.flatMap((ref) => {
                const transformed = transformRelativeSignalRef(ref, droppedIds, droppedKeys, renameByKey);
                if (transformed === null) {
                    functionRefCount += 1;
                    return [];
                }
                return [transformed];
            });
            return { ...functionSignal, signals: kept };
        }),
    }));
    return {
        payload: {
            ...payload,
            signals: signalsWithTies,
            pinout,
            boundaries,
            functions,
        },
        dropped: {
            signalIds: [...droppedIds],
            boundaryIds: droppedBoundaryIds,
            tieCount,
            functionRefCount,
            duplicateNameIds,
        },
    };
}
//# sourceMappingURL=apply-signal-consolidation-map.js.map